import React, { useState } from 'react';
import { X, Trash2, Loader2, AlertTriangle, Plus, Pause, ArrowRightLeft } from 'lucide-react';
import { useNivelesQuery } from '../../services/hooks/useNiveles';
import { useEliminarPlazaMutation } from '../../services/hooks/usePlazas';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { Plaza } from '../../types/plaza';
import { useUser } from '@clerk/clerk-react';
// import ProgressPlazaModal from './ProgressPlazaModal';
import AgregarPlazaCliente from './AgregarPlazaCliente';
import { useSafeMounted } from '../../services/hooks';
import ModalEsperaPlazas from './ModalEsperaPlazas';

interface GestionarPlazasClienteProps {
    clientId: string;
    plazasActuales: Plaza[];
    suscripcionId: string; 
    onClose: () => void;
}

const GestionarPlazasCliente: React.FC<GestionarPlazasClienteProps> = ({ 
    clientId, 
    suscripcionId,
    plazasActuales, 
    onClose 
}) => {


    const isMounted = useSafeMounted();
    // Si el componente está desmontado, no renderizar nada
    if (!isMounted.current) return null;

    // Contexto de autenticación para obtener usuario actual
    const { user } = useUser();
    
    
    // Estado para modal de confirmación
    const [plazaAEliminar, setPlazaAEliminar] = useState<string | null | undefined>(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    // Estado para mostrar el modal de agregar plaza
    const [showAgregarPlaza, setShowAgregarPlaza] = useState(false);

    // M5: plaza que se está moviendo/editando (null = ninguna)
    const [plazaAMover, setPlazaAMover] = useState<Plaza | null>(null);
    
    // Estado para mostrar el modal de gestión de espera
    const [showModalEspera, setShowModalEspera] = useState(false);
    
    // Mutaciones
    const eliminarPlazaMutation = useEliminarPlazaMutation();
    
    
    // Consulta de progreso
    const { data: niveles, isLoading: isLoadingNiveles } = useNivelesQuery();
    // const { data: progressData, isSuccess } = usePlazaEventoProgress(eliminacionSessionId || undefined);
    
    // Función para manejar eliminación de plaza
    const handleRemovePlaza = async () => {
        if (!plazaAEliminar) return;
        
        try {
            const eliminada = await eliminarPlazaMutation.mutateAsync({
                plazaId: plazaAEliminar,
                clientId,
                updatedBy: user?.firstName || user?.username || 'Usuario del sistema'
            });

            // El back ya recalculó el importe con una plaza menos (antes lo
            // pedía esta pantalla en otra petición aparte).
            const precio = eliminada?.precio;
            if (precio?.estado === 'recalculada' && precio.importe !== precio.importeAnterior) {
                toast.info(`Importe actualizado: ${precio.importeAnterior} € → ${precio.importe} € (${precio.frecuencia})`, { autoClose: 6000 });
            }

            // Cerrar modal de confirmación
            setConfirmModalOpen(false);

            // También invalidamos consultas para asegurar datos actualizados
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['client', clientId] });
            queryClient.invalidateQueries({ queryKey: ['eventos'] });
            
        } catch (error) {
            console.error('Error al eliminar plaza:', error);
            // El toast de error lo maneja el hook
        }
    };
    

    // Filtrar solo plazas activas
    const plazasActivasActuales = plazasActuales.filter(plaza => plaza.activo !== false);

    // Verificar si hay plazas en espera
    const hayPlazasEnEspera = plazasActivasActuales.some(plaza => plaza.enEspera);
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Gestionar Plazas</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>
                <div className='p-2'>
                    <p>En este apartado podrás borrar y asignar plazas.</p>
                    <p>Ten en cuenta que al borrar una plaza, se removerá al cliente de las clases futuras.</p>
                    <p>Para cambiar el día u hora de una plaza, usa el botón <strong>Mover</strong> (↹): el cliente se re-asignará a las clases del nuevo horario automáticamente.</p>
                </div>
                {/* Plazas actuales */}
                <div className="mb-8">
                    <div className='flex justify-items-start gap-4'>
                        <button
                            onClick={() => setShowAgregarPlaza(true)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 my-4 rounded-md flex items-center text-sm"
                        >
                            <Plus size={16} className="mr-1"/> Agregar plaza
                        </button>
                        <button
                            onClick={() => setShowModalEspera(true)}
                            className={`${hayPlazasEnEspera ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-blue-500 hover:bg-blue-600'} text-white px-3 py-1 my-4 rounded-md flex items-center text-sm`}
                        >
                            <Pause size={16} className="mr-1"/> 
                            {hayPlazasEnEspera ? 'Gestionar Espera' : 'Poner en Espera'}
                        </button>
                    </div>
                    
                    
                    <h3 className="text-lg font-semibold mb-4">Plazas Actuales</h3>
                    
                    {isLoadingNiveles ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="animate-spin" />
                        </div>
                    ) : plazasActivasActuales.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {plazasActivasActuales.map(plaza => (
                                <div 
                                    key={plaza._id} 
                                    className="border p-4 rounded-lg flex justify-between bg-white shadow-sm"
                                >
                                    <div>
                                        <div className="font-medium">{plaza.dia} - {plaza.horaInicio}</div>
                                        <div>
                                            {typeof plaza.nivel_id === 'object' 
                                                ? plaza.nivel_id.nombre 
                                                : niveles?.find(n => n._id === plaza.nivel_id)?.nombre || 'Desconocido'
                                            }
                                        </div>
                                        {plaza.enEspera && (
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                                En espera
                                            </span>
                                        )}
                                        {plaza.observaciones && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                {plaza.observaciones}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <button
                                            onClick={() => setPlazaAMover(plaza)}
                                            className="text-blue-500 hover:text-blue-700"
                                            title="Mover / editar horario"
                                        >
                                            <ArrowRightLeft size={18} />
                                        </button>
                                        <button
                                            onClick={() => {
                                                setPlazaAEliminar(plaza._id);
                                                setConfirmModalOpen(true);
                                            }}
                                            className="text-red-500 hover:text-red-700"
                                            title="Eliminar plaza"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500">No tiene plazas reservadas</p>
                    )}
                </div>

                {/* Aquí irá la sección para añadir nuevas plazas (temporalmente omitida) */}
                <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500">
                        * Para añadir nuevas plazas, utilice el botón "Agregar plazas".
                    </p>
                    <button 
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    >
                        Cerrar
                    </button>
                </div>

                {/* Modal de confirmación de eliminación */}
                {confirmModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <div className="flex items-center mb-4">
                                <AlertTriangle className="text-yellow-500 mr-2" size={24} />
                                <h3 className="text-lg font-bold">Confirmar Eliminación</h3>
                            </div>
                            
                            <p className="mb-6">
                                Esta acción eliminará la plaza del cliente y lo desvinculará de todos los eventos futuros relacionados con esta plaza. ¿Está seguro que desea continuar?
                            </p>
                            
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setConfirmModalOpen(false);
                                        setPlazaAEliminar(null);
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleRemovePlaza}
                                    disabled={eliminarPlazaMutation.isPending}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                                >
                                    {eliminarPlazaMutation.isPending && (
                                        <Loader2 className="animate-spin mr-2" size={16} />
                                    )}
                                    {eliminarPlazaMutation.isPending ? "Eliminando..." : "Eliminar"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de agregar plaza */}
                {showAgregarPlaza && (
                    <AgregarPlazaCliente
                        clientId={clientId}
                        suscripcionId={suscripcionId}
                        plazasActuales={plazasActuales}
                        onClose={() => setShowAgregarPlaza(false)}
                    />
                )}

                {/* M5: Modal de mover/editar plaza (reutiliza AgregarPlazaCliente en modo mover) */}
                {plazaAMover && (
                    <AgregarPlazaCliente
                        clientId={clientId}
                        suscripcionId={suscripcionId}
                        plazaToMove={plazaAMover}
                        plazasActuales={plazasActuales}
                        onClose={() => setPlazaAMover(null)}
                    />
                )}

                {/* Modal de gestión de espera */}
                {showModalEspera && (
                    <ModalEsperaPlazas
                        clientId={clientId}
                        onClose={() => setShowModalEspera(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default GestionarPlazasCliente;